import { useEffect, useState } from "react";

/**
 * Stale-Clerk-cookie self-recovery.
 *
 * ## The bug this fixes
 * After the June 2026 migration from the Replit-managed Clerk instance to the
 * operator's OWN external Clerk (`pk_live` bound to `clerk.metalui.co.uk`), a
 * browser that was used during the OLD instance era keeps sending a stale
 * `__client` / `__session` / `__client_uat` cookie minted by the PREVIOUS Clerk
 * instance. The new instance rejects that foreign client token with
 * `authorization_invalid`, and clerk-js never finishes initialising — so
 * `useAuth().isLoaded` stays `false` forever and the app hangs on "Loading…".
 *
 * The tell-tale signature (and why this is the right layer): sign-in works in a
 * fresh incognito window but fails in the normal Chrome profile. Crucially,
 * Chrome's "Clear browsing history" does NOT clear cookies unless the user also
 * ticks "Cookies and other site data" — so an owner who "cleared history" is
 * still sending the stale cookie. Because incognito (no cookies) works, the
 * account data and server/Clerk config are healthy; only the browser-local
 * cookie state is poisoned.
 *
 * ## The recovery
 * When clerk-js fails to initialise within a timeout we wipe every
 * JS-accessible Clerk cookie (across the host and the registrable
 * `metalui.co.uk` parent domain) plus Clerk's local/session storage, then
 * reload ONCE. That reproduces the incognito-clean state without the user
 * touching browser settings. The reload is guarded by a one-shot
 * `sessionStorage` flag so a genuinely-broken environment can never reload-loop;
 * if the very next load still hangs, the caller shows a friendly manual
 * recovery screen instead of spinning forever.
 *
 * Note: a production Clerk instance may set the `__client` cookie `httpOnly` on
 * the FAPI sub-domain, which page JS cannot delete. Clearing the app-domain
 * cookies (`__client_uat`, `__session`) is usually enough for clerk-js to treat
 * the client as absent and re-handshake; the manual recovery screen covers the
 * residual case with plain-language "reset" guidance.
 */

/**
 * How long to wait for clerk-js to initialise before assuming it is wedged.
 *
 * This is deliberately conservative. The genuine stale-cookie lockout NEVER
 * resolves (the foreign client token is rejected and clerk-js stays wedged
 * forever), so waiting longer costs that case only a little extra latency. A
 * healthy user behind a brief network hiccup or a slow FAPI response, by
 * contrast, WILL finish initialising given enough time — and the recovery's
 * first action is a destructive cookie wipe that would log a healthy user out.
 * With the `clerk.on("status")` error-signal path active (which is the normal
 * production case), the timer's only action is showing the non-destructive
 * recovery screen — no cookies are wiped — so a shorter window is safe and
 * strictly better for the user. 4 s gives Clerk enough time to finish a normal
 * init on a slow connection while not leaving genuinely-stuck users staring at
 * a blank "Loading…" for long. If clerk loads at any point — even after this
 * window elapses and the reset screen is showing — `useClerkLoadWatchdog`
 * releases the user automatically (see its `isLoaded` self-heal below).
 */
export const CLERK_LOAD_TIMEOUT_MS = 4_000;

/**
 * The production root domain the `pk_live` key is bound to. The stale-cookie
 * lockout only happens on these hosts (where real users sign in and where the
 * pre-migration cookie was minted). On localhost / *.replit.dev / *.replit.app
 * dev previews, clerk-js fails for a DIFFERENT reason — the production key is
 * domain-locked to metalui.co.uk — and a cookie wipe + reload would neither help
 * nor be appropriate, so we deliberately leave dev/preview behaviour untouched.
 */
export function isRecoveryEligibleHost(): boolean {
  if (typeof window === "undefined") return false;
  const host = window.location.hostname;
  return host === "metalui.co.uk" || host.endsWith(".metalui.co.uk");
}

/** One-shot guard so auto-recovery reloads AT MOST once per tab session. */
const RECOVERY_GUARD_KEY = "metalui:clerk-recovery-attempted";

/** Cookie-name prefixes Clerk uses (plus per-instance hashed suffixes). */
const CLERK_COOKIE_PREFIXES = ["__client", "__session", "__clerk", "__refresh"] as const;

function isClerkCookieName(name: string): boolean {
  return CLERK_COOKIE_PREFIXES.some((p) => name === p || name.startsWith(p));
}

/**
 * Build the set of `domain=` candidates to try when expiring a cookie. A cookie
 * can only be deleted with the exact domain/path it was set on, and we don't
 * know which Clerk used, so we attempt the host-only form plus every parent
 * domain up to the registrable root (e.g. for `everyday-bullion.metalui.co.uk`:
 * host-only, `.everyday-bullion.metalui.co.uk`, `.metalui.co.uk`, `.co.uk`).
 */
function domainCandidates(): Array<string | undefined> {
  const host = window.location.hostname;
  const candidates: Array<string | undefined> = [undefined, host];
  const parts = host.split(".");
  for (let i = 1; i < parts.length - 1; i++) {
    candidates.push("." + parts.slice(i).join("."));
  }
  // Also the full host with a leading dot (covers subdomain-scoped cookies).
  candidates.push("." + host);
  return candidates;
}

function expireCookie(name: string): void {
  const expiry = "expires=Thu, 01 Jan 1970 00:00:00 GMT";
  for (const domain of domainCandidates()) {
    const domainPart = domain ? `; domain=${domain}` : "";
    // path=/ is what Clerk uses; also clear the current path just in case.
    document.cookie = `${name}=; ${expiry}; path=/${domainPart}; SameSite=Lax`;
    document.cookie = `${name}=; ${expiry}; path=/${domainPart}`;
  }
}

/**
 * Ask the server to expire any stale Clerk cookie it can see on the incoming
 * `Cookie` header — including ones marked `HttpOnly`, which are invisible to
 * (and therefore un-clearable by) `document.cookie` in `clearClerkClientState`
 * below. See `artifacts/api-server/src/routes/legacyCookieReset.ts` for the
 * full rationale.
 *
 * Returns a Promise so callers can await completion before navigating. Uses a
 * short AbortController timeout (3 s) so a slow server never holds up recovery
 * indefinitely. The catch ensures a network failure never aborts the caller's
 * recovery flow.
 */
export async function clearLegacyCookiesServerSide(): Promise<void> {
  if (typeof fetch === "undefined") return;
  try {
    const base = (import.meta.env.BASE_URL as string) || "/";
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 3_000);
    try {
      await fetch(`${base}api/auth/legacy-cookie-reset`, {
        method: "POST",
        credentials: "include",
        keepalive: true,
        signal: controller.signal,
      });
    } finally {
      window.clearTimeout(timeoutId);
    }
  } catch {
    /* best-effort — client-side sweep already covers the non-HttpOnly case */
  }
}

/**
 * Best-effort wipe of all JS-accessible Clerk client state (cookies + storage).
 * Every step is wrapped so a single failure (e.g. storage access denied in a
 * locked-down profile) never aborts the rest of the cleanup.
 */
export function clearClerkClientState(): void {
  if (typeof document === "undefined") return;
  try {
    const cookies = document.cookie ? document.cookie.split(";") : [];
    for (const raw of cookies) {
      const name = raw.split("=")[0]?.trim();
      if (name && isClerkCookieName(name)) {
        try {
          expireCookie(name);
        } catch {
          /* keep clearing the rest */
        }
      }
    }
  } catch {
    /* document.cookie unavailable — nothing to clear */
  }

  for (const store of [window.localStorage, window.sessionStorage]) {
    try {
      const keys: string[] = [];
      for (let i = 0; i < store.length; i++) {
        const key = store.key(i);
        if (key && /clerk/i.test(key)) keys.push(key);
      }
      // Never remove our own recovery guard while sweeping sessionStorage.
      for (const key of keys) {
        if (key !== RECOVERY_GUARD_KEY) store.removeItem(key);
      }
    } catch {
      /* storage blocked — ignore */
    }
  }

  // Best-effort IndexedDB sweep: delete any database whose name matches a
  // Clerk prefix. `indexedDB.databases()` is not supported on older browsers
  // or Safari <15 — degrade silently if unavailable.
  try {
    if (typeof indexedDB !== "undefined" && typeof indexedDB.databases === "function") {
      void indexedDB.databases().then((dbs) => {
        for (const db of dbs) {
          if (db.name && /^(__clerk|clerk)/i.test(db.name)) {
            try {
              indexedDB.deleteDatabase(db.name);
            } catch {
              /* best-effort per-db */
            }
          }
        }
      }).catch(() => {
        /* databases() failed — nothing to sweep */
      });
    }
  } catch {
    /* indexedDB unavailable — ignore */
  }
}

/**
 * Persistent (not per-tab) guard for the PRE-EMPTIVE cookie sweep below. Uses
 * `localStorage` (survives across tabs/sessions) rather than the per-tab
 * `sessionStorage` guard used by the reactive wedge-recovery above, because
 * this only needs to run ONCE per browser, ever.
 */
const PREFLIGHT_SWEEP_KEY = "metalui:clerk-legacy-cookie-swept-v2";

/**
 * Pre-emptively sweep leftover Clerk cookies BEFORE the user starts a social
 * (e.g. Google) sign-in redirect.
 *
 * ## Why this is a separate fix from `useClerkLoadWatchdog`
 * The watchdog above only catches the case where clerk-js fails to
 * initialise INSIDE our SPA (`isLoaded` never becomes `true`). But the
 * confirmed failure mode for Google sign-in is different: clerk-js loads
 * FINE, the user clicks "Continue with Google", completes auth with Google,
 * and the browser is redirected back to `https://clerk.metalui.co.uk/v1/
 * oauth_callback` — a full navigation AWAY from our React app. If the
 * browser still carries a stale `__client`/`__session` cookie from the
 * pre-migration Clerk instance, THAT server-side endpoint rejects it with
 * `authorization_invalid` and renders a raw JSON error page. Our React
 * watchdog code never runs there, so it can't help retroactively.
 *
 * The fix has to happen BEFORE the redirect leaves our app: once clerk-js
 * has loaded and confirmed the browser is NOT currently signed in (so there
 * is no legitimate active session to protect — a real active session would
 * have already redirected the user away from the sign-in page), we
 * pre-emptively wipe any leftover JS-accessible Clerk cookies. That way, by
 * the time the user clicks a social sign-in button, the browser has nothing
 * stale left to send on the cross-origin callback.
 *
 * Runs at most once per browser (persistent guard), only on the production
 * root domain, and only once Clerk has positively confirmed "not signed in"
 * — never while a session might still be valid.
 *
 * The guard key is versioned (`-v2`): the original `-v1` sweep could only
 * clear JS-visible cookies, so a browser carrying an `HttpOnly` stale cookie
 * would exhaust its one-shot attempt without ever actually fixing anything,
 * and then never retry. Bumping the version gives every such browser exactly
 * one more automatic attempt that ALSO calls the new server-side clearer
 * (`clearLegacyCookiesServerSide`), which can reach `HttpOnly` cookies.
 */
export function useStaleCookiePreflightSweep(isLoaded: boolean, isSignedIn: boolean | undefined | null): void {
  useEffect(() => {
    if (!isLoaded || isSignedIn) return;
    if (!isRecoveryEligibleHost()) return;
    try {
      if (window.localStorage.getItem(PREFLIGHT_SWEEP_KEY) === "1") return;
    } catch {
      return; // no localStorage — skip rather than sweep on every render
    }
    // Sweep BEFORE setting the guard: clearClerkClientState() removes every
    // localStorage key matching /clerk/i (our own guard key included, since
    // it deliberately mentions "clerk" for discoverability), so setting the
    // guard first would just have it wiped out immediately afterwards.
    clearClerkClientState();
    clearLegacyCookiesServerSide();
    try {
      window.localStorage.setItem(PREFLIGHT_SWEEP_KEY, "1");
    } catch {
      /* storage blocked — best-effort guard only, sweep already happened */
    }
  }, [isLoaded, isSignedIn]);
}

/**
 * Attempt automatic recovery from a wedged clerk-js init.
 *
 * - First call in a tab session: wipe Clerk state, set the one-shot guard, and
 *   reload. Returns `"reloading"`.
 * - Subsequent call (guard already set): does NOT reload — the previous attempt
 *   didn't help, so we hand control back to the caller to show manual recovery.
 *   Returns `"exhausted"`.
 */
export function attemptClerkRecovery(): "reloading" | "exhausted" {
  let alreadyTried = false;
  try {
    alreadyTried = window.sessionStorage.getItem(RECOVERY_GUARD_KEY) === "1";
  } catch {
    // No sessionStorage — fall back to a query flag so we still avoid a loop.
    alreadyTried = new URLSearchParams(window.location.search).has("__clerk_reset");
  }

  if (alreadyTried) return "exhausted";

  try {
    window.sessionStorage.setItem(RECOVERY_GUARD_KEY, "1");
  } catch {
    /* ignore — the query-flag fallback below still guards the loop */
  }
  clearClerkClientState();
  clearLegacyCookiesServerSide();
  // Reload to re-init clerk-js from the now-clean cookie jar.
  try {
    const url = new URL(window.location.href);
    url.searchParams.set("__clerk_reset", "1");
    window.location.replace(url.toString());
  } catch {
    window.location.reload();
  }
  return "reloading";
}

/**
 * Force a manual recovery from the recovery screen: clear the one-shot guard so
 * the wipe-and-reload path runs fresh, wipe all Clerk state (client-side AND
 * server-side HttpOnly cookies), then navigate to /sign-in. Navigating to
 * /sign-in (not back to the original URL) breaks the `?__clerk_reset=1`
 * re-append loop that Clerk's internal error handler produces.
 *
 * Returns a Promise so the caller can await completion and handle navigation
 * failures (e.g. re-enable the reset button in a finally block).
 */
export async function forceClerkRecovery(): Promise<void> {
  try {
    window.sessionStorage.removeItem(RECOVERY_GUARD_KEY);
  } catch {
    /* ignore */
  }
  clearClerkClientState();
  await clearLegacyCookiesServerSide();
  try {
    const base = ((import.meta.env.BASE_URL as string) || "/").replace(/\/$/, "");
    window.location.replace(base + "/sign-in");
  } catch {
    window.location.reload();
  }
}

/**
 * clerk-js lifecycle status. Mirrors `@clerk/types`' `ClerkStatus` so we don't
 * take a direct dependency on it from this lib. `"error"` is the one value that
 * means "Clerk failed to initialise" — the confirmed init-failure signal we use
 * to fire recovery WITHOUT waiting on a timeout.
 */
type ClerkInitStatus = "degraded" | "error" | "loading" | "ready";

/**
 * The minimal slice of the clerk-js singleton this watchdog needs to subscribe
 * to init-status changes. Structurally satisfied by the object returned from
 * `useClerk()`; kept narrow (and every member optional) so the watchdog still
 * works when the event API is absent and so the hook stays trivially testable
 * with a hand-rolled fake.
 */
export interface ClerkInitSignalSource {
  status?: ClerkInitStatus;
  on?: (
    event: "status",
    handler: (status: ClerkInitStatus) => void,
    opt?: { notify: boolean },
  ) => void;
  off?: (event: "status", handler: (status: ClerkInitStatus) => void) => void;
}

/**
 * Watch clerk-js initialisation. Pass `isLoaded` from `useAuth()` and, when
 * available, the `useClerk()` singleton so the watchdog can listen for the
 * precise init-failure signal instead of guessing from a timeout alone.
 *
 * ## Why an error signal, not just a timeout
 * A pure timeout cannot tell a genuine stale-cookie lockout (which never
 * resolves) apart from a healthy session behind a slow/transient FAPI hiccup
 * (which WILL resolve given time). Acting on the timeout alone therefore wipes
 * cookies — logging the user out — whenever a healthy init merely runs long.
 * clerk-js exposes a `status` lifecycle (`loading → ready`, or `→ error` when
 * init genuinely fails). We subscribe to it and run the destructive
 * wipe + reload ONLY on a confirmed `"error"`, so a healthy-but-slow session is
 * never wiped.
 *
 * ## Behaviour
 * - **Confirmed failure** (`status === "error"`, delivered live or immediately
 *   via `notify`): run `attemptClerkRecovery()` — the one-shot destructive
 *   wipe + reload.
 * - **Timeout with the error signal available**: we do NOT wipe (the session
 *   may be healthy-but-slow). Instead we surface the NON-destructive manual
 *   recovery screen (`stuck`), which the user can act on if they choose. This
 *   is the escape hatch for the rare wedge that never emits `"error"`.
 * - **No error signal available** (older clerk / not yet mounted): fall back to
 *   the original behaviour — the timeout itself drives `attemptClerkRecovery()`.
 *
 * ## Graceful degradation (false-positive / transient case)
 * `isLoaded` is an effect dependency, so the moment clerk-js finishes
 * initialising the effect re-runs and unconditionally clears `stuck` — even
 * after the reset screen is showing. A brief hiccup or slow FAPI response that
 * resolves on its own therefore releases the user automatically. The destructive
 * wipe + reload only ever runs once per tab session (guarded by
 * `RECOVERY_GUARD_KEY`), so a transient can cost at most a single reload.
 */
export function useClerkLoadWatchdog(
  isLoaded: boolean,
  clerk?: ClerkInitSignalSource | null,
): { stuck: boolean } {
  const [stuck, setStuck] = useState(false);
  useEffect(() => {
    if (isLoaded || !isRecoveryEligibleHost()) {
      // Clerk is healthy (or we're on a non-production host where recovery is
      // never appropriate): make sure we are not pinned on the reset screen.
      setStuck(false);
      return;
    }

    // One-shot guard so the live error event and the timeout backstop can't both
    // fire recovery for the same wedge.
    let handled = false;
    const runDestructiveRecovery = () => {
      if (handled) return;
      handled = true;
      if (attemptClerkRecovery() === "exhausted") setStuck(true);
    };

    const hasErrorSignal = !!clerk && typeof clerk.on === "function";

    let unsubscribe: (() => void) | undefined;
    if (hasErrorSignal) {
      const onStatus = (status: ClerkInitStatus) => {
        // Only a hard "error" means init genuinely failed. "degraded" is
        // partially-operational and must NOT trigger a wipe.
        if (status === "error") runDestructiveRecovery();
      };
      try {
        // `notify: true` replays the current status synchronously, covering the
        // case where clerk already errored before we subscribed.
        clerk!.on!("status", onStatus, { notify: true });
        unsubscribe = () => {
          try {
            clerk!.off?.("status", onStatus);
          } catch {
            /* best-effort */
          }
        };
      } catch {
        // Subscription failed — behave as if the signal were unavailable.
      }
    }

    const timer = window.setTimeout(() => {
      if (handled) return;
      if (hasErrorSignal) {
        // We have a precise failure signal and it has NOT fired, so this is
        // most likely a healthy-but-slow init. Do not wipe; surface the
        // non-destructive manual recovery screen as the escape hatch.
        handled = true;
        setStuck(true);
      } else {
        // No error signal to rely on — preserve the original timeout-driven
        // destructive recovery.
        runDestructiveRecovery();
      }
    }, CLERK_LOAD_TIMEOUT_MS);

    return () => {
      window.clearTimeout(timer);
      unsubscribe?.();
    };
  }, [isLoaded, clerk]);
  return { stuck };
}
